import { useMemo, useEffect } from "react";
import { useAtom, useAtomValue } from "jotai";
import { sortBy } from "lodash";
import { FinancialProductReadDTO } from "../types/FinancialApplication";
import FinancialApplicationApiClient from "../api/financialApplicationApiClient";
import { 
  financialProductsAtom, 
  financialProductsLoadingAtom, 
  financialProductsErrorAtom, 
  financialProductsRefreshAtom 
} from "../atoms/financialProductsAtom";

export interface SegmentProductCombination {
  customerSegment: string;
  productType: string;
  productTypeOrder: number;
  tabKey: string;
  displayName: string;
}

interface FinancialProductsModule {
  products: FinancialProductReadDTO[];
  customerSegments: string[];
  productTypes: string[];
  segmentProductCombinations: SegmentProductCombination[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Helper function to convert ProductType to tab key format
const productTypeToTabKey = (productType: string): string => {
  return productType.toLowerCase().replace(/\s+/g, '-');
};

export const useFinancialProducts = (
  apiClient: FinancialApplicationApiClient
): FinancialProductsModule => {
  // Use Jotai atoms instead of local state
  const products = useAtomValue(financialProductsAtom);
  const loading = useAtomValue(financialProductsLoadingAtom);
  const error = useAtomValue(financialProductsErrorAtom);
  const [, refreshProducts] = useAtom(financialProductsRefreshAtom);

  // Extract unique customer segments from products
  const customerSegments = useMemo(() => {
    const segments = new Set(products.map(product => product.customerSegment));
    return Array.from(segments).sort();
  }, [products]);

  // Extract unique product types from products
  const productTypes = useMemo(() => {
    const types = new Set(products.map(product => product.productType));
    return Array.from(types).sort();
  }, [products]);

  // Extract unique customer segment + product type combinations
  const segmentProductCombinations = useMemo(() => {
    const combinations = new Map<string, SegmentProductCombination>();
    
    products.forEach(product => {
      const key = `${product.customerSegment}-${product.productType}`;
      if (!combinations.has(key)) {
        const customerSegmentKey = product.customerSegment.toLowerCase();
        const productTypeKey = productTypeToTabKey(product.productType);
        const tabKey = `${customerSegmentKey}-${productTypeKey}`;
        
        combinations.set(key, {
          customerSegment: product.customerSegment,
          productType: product.productType,
          productTypeOrder: product.productTypeOrder,
          tabKey,
          displayName: product.productType,
        });
      }
    });
    
    return sortBy(Array.from(combinations.values()), ['customerSegment', 'productTypeOrder']);
  }, [products]);

  const refetch = () => {
    refreshProducts(apiClient, true);
  };

  // Initial fetch if products haven't been loaded yet
  useEffect(() => {
    if (products.length === 0 && !loading) {
      refreshProducts(apiClient);
    }
  }, [apiClient, products.length, loading, refreshProducts]);

  return {
    products,
    customerSegments,
    productTypes,
    segmentProductCombinations,
    loading,
    error,
    refetch,
  };
}; 